import { ApolloLink, type FetchResult, Observable, type Operation } from '@apollo/client';
import { print } from 'graphql';
import { type ClientOptions, createClient } from 'graphql-sse';

import { trackEvent } from '@/lib/analytics';
import { getWsTimelineSince, recordWsEvent } from '@/lib/ws/ws-telemetry';

const isEventStream = (response: Response) => response.headers.get('content-type')?.includes('text/event-stream');

// SSE は再接続せずにエラーで終わるため、WebSocket のように 120 秒後のタイムアウトとして数えられない。
// WebSocket と比べるときに失敗を取りこぼさないよう、分析のイベントとして残す（本文や原因の文言は含めない）。
const reportSubscriptionFailure = (startedAt: number, resultCount: number, error: unknown) => {
  try {
    trackEvent('subscription_failed', {
      elapsed_ms: Date.now() - startedAt,
      result_count: resultCount,
      error_name: error instanceof Error ? error.name : 'unknown',
      ...getWsTimelineSince(startedAt),
    });
  } catch {
    // 送信失敗は無視（観測の失敗で本処理を止めない）
  }
};

/**
 * 購読を SSE（POST + text/event-stream）で受ける Apollo リンク。購読ごとに POST を 1 本張り、
 * 通常の API 呼び出しと同じ HTTP/2 の接続に載せて、新しい接続を作らずに済ませる狙い。送信そのものが購読なので、再送すると Dify への送信が
 * 二重になる。待ち時間切れ・途中の切断・SSE でない応答は、`retryAttempts: 0` で再送せずエラーで終わらせる。
 */
export class SSELink extends ApolloLink {
  private readonly options: ClientOptions;

  constructor(options: ClientOptions) {
    super();
    this.options = options;
  }

  request(operation: Operation): Observable<FetchResult> {
    return new Observable<FetchResult>(subscriber => {
      const startedAt = Date.now();
      let resultCount = 0;
      const { fetchFn, ...options } = this.options;

      // graphql-sse の fetchFn の型は unknown。ここで渡されるのは、fetch 互換の関数だけ
      const baseFetch = (fetchFn as typeof fetch | undefined) ?? fetch;
      const guardedFetch: typeof fetch = async (input, init) => {
        const response = await baseFetch(input, init);
        if (response.ok && !isEventStream(response)) {
          void response.body?.cancel();
          throw new Error('The GraphQL endpoint did not respond with text/event-stream');
        }
        return response;
      };

      const client = createClient({
        ...options,
        fetchFn: guardedFetch,
        retryAttempts: 0,
        on: { connecting: () => recordWsEvent('connecting', { transport: 'sse' }) },
      });

      const unsubscribe = client.subscribe(
        { ...operation, query: print(operation.query) },
        {
          next: result => {
            resultCount += 1;
            // graphql-sse の ExecutionResult は、Apollo の FetchResult と同じ形（data / errors / extensions）
            subscriber.next(result as FetchResult);
          },
          complete: () => {
            recordWsEvent('closed', { transport: 'sse' });
            subscriber.complete();
          },
          error: error => {
            recordWsEvent('error', { transport: 'sse' });
            reportSubscriptionFailure(startedAt, resultCount, error);
            subscriber.error(error);
          },
        },
      );

      return () => {
        unsubscribe();
        void client.dispose();
      };
    });
  }
}
